"use client"
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { handleNavigationWithPaywall } from "@/utils/paywall-navigation-utils";

import { ChevronRight, type LucideIcon } from "lucide-react"
import { isMenuItemActive, isGroupActive } from "@/utils/sidebar-utils"

import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
    SidebarGroup,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuAction,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
} from "@/components/ui/sidebar"

function Reporting({
    items,
}: {
    items: {
        title: string
        url: string
        icon: LucideIcon
        isActive?: boolean
        routeName?: string
        items?: {
            title: string
            url: string
            icon: LucideIcon
            onClick?: (e: React.MouseEvent) => void
            routeName?: string
        }[]
    }[]
}) {
    const location = useLocation();
    const navigate = useNavigate();
    const currentPath = location.pathname.substring(1); // Get current path without leading slash
    return (
        <SidebarGroup>
            <SidebarMenu>
                {items.map((item) => (
                    <Collapsible key={item.title} asChild defaultOpen={item.isActive || isGroupActive(item.items, currentPath)}>
                        <SidebarMenuItem>
                            {item.items?.length ? (
                                <>
                                    <CollapsibleTrigger asChild>
                                        <SidebarMenuButton asChild tooltip={item.title} isActive={isGroupActive(item.items, currentPath)}>
                                            <button type="button" className="w-full flex items-center gap-2 text-left" onClick={(e) => {
                                        handleNavigationWithPaywall(item.url, navigate, e, item.routeName);
                                    }}
                                    onAuxClick={(e) => {
                                        // Middle click
                                        handleNavigationWithPaywall(item.url, navigate, e, item.routeName);
                                    }}
                                    onContextMenu={(e) => {
                                        // Right click - prevent context menu and open in new tab
                                        e.preventDefault();
                                        handleNavigationWithPaywall(item.url, navigate, e, item.routeName);
                                    }}
                                    data-sentry-unmask>
                                                <item.icon className="text-nav-title" data-sentry-unmask/>
                                                <span className="text-nav-title" data-sentry-unmask>{item.title}</span>
                                            </button>
                                        </SidebarMenuButton>
                                    </CollapsibleTrigger>
                                    <CollapsibleTrigger asChild>
                                        <SidebarMenuAction className="data-[state=open]:rotate-90">
                                            <ChevronRight />
                                            <span className="sr-only">Toggle</span>
                                        </SidebarMenuAction>
                                    </CollapsibleTrigger>
                                </>
                            ) : (
                                <SidebarMenuButton asChild tooltip={item.title}>
                                    <a href={item.url} onClick={(e) => handleNavigationWithPaywall(item.url, navigate, e, item?.routeName)} data-sentry-unmask>
                                        <item.icon data-sentry-unmask />
                                        <span data-sentry-unmask>{item.title}</span>
                                    </a>
                                </SidebarMenuButton>
                            )}
                            {item.items?.length ? (
                                <CollapsibleContent>
                                    <SidebarMenuSub>
                                        {item.items?.map((subItem) => (
                                            <SidebarMenuSubItem key={subItem.title}>
                                                <SidebarMenuSubButton asChild isActive={isMenuItemActive(subItem.url, currentPath)}>
                                                    <button type="button" className="w-full flex items-center gap-2 text-left" onClick={(e) => {
                                                    // If a custom click handler is provided, use it and skip navigation
                                                    if (typeof subItem.onClick === 'function') {
                                                        subItem.onClick(e);
                                                        return;
                                                    }
                                                    handleNavigationWithPaywall(subItem.url, navigate, e, subItem.routeName);
                                                }}
                                                onAuxClick={(e) => {
                                                    // Middle click - skip if custom handler, otherwise open in new tab
                                                    if (typeof subItem.onClick === 'function') {
                                                        subItem.onClick(e);
                                                        return;
                                                    }
                                                    handleNavigationWithPaywall(subItem.url, navigate, e, subItem.routeName);
                                                }}
                                                onContextMenu={(e) => {
                                                    // Right click - prevent context menu and open in new tab
                                                    e.preventDefault();
                                                    if (typeof subItem.onClick === 'function') {
                                                        subItem.onClick(e);
                                                        return;
                                                    }
                                                    handleNavigationWithPaywall(subItem.url, navigate, e, subItem.routeName);
                                                }}
                                                data-sentry-unmask>
                                                        <subItem.icon data-sentry-unmask />
                                                        <span data-sentry-unmask>{subItem.title}</span>
                                                    </button>
                                                </SidebarMenuSubButton>
                                            </SidebarMenuSubItem>
                                        ))}
                                    </SidebarMenuSub>
                                </CollapsibleContent>
                            ) : null}
                        </SidebarMenuItem>
                    </Collapsible>
                ))}
            </SidebarMenu>
        </SidebarGroup>
    )
}

export { Reporting }
